import React from 'react'
import Pt from 'prop-types'
import { Button, Col, Panel, Row } from 'react-bootstrap'
import { IndexLinkContainer } from 'react-router-bootstrap'
import Ipt from 'react-immutable-proptypes'

import Icon from 'internal-components/src/components/Icon'
import {
  OfferFilterDataRecord,
  OfferFilterRecord,
  OfferRecord,
  ProductRecord,
} from 'internal-components/src/records'

import Filter from './Filter'
import OfferGrid from './OfferGrid'
import ProductDetails from './ProductDetails'

import './OfferSelector.css'
import {makeSelectQualificationResults} from "../../redux/modules/signupEdit/selectors";
import {connect} from "react-redux";
import {
  makeSelectOfferFilterData,
  makeSelectProduct
} from "../../../deps/internal-components/src/redux/modules/product/selectors";
import {makeSelectLoading} from "../../../deps/internal-components/src/redux/modules/loading";
import {filterOffer} from "../../../deps/internal-components/src/records/OfferFilter";

function OfferSelector({
  addressSupplied = false,
  currentOffer,
  currentOfferVisible,
  currentOfferId,
  currentProduct,
  filterData,
  filter,
  hideAddressButton = false,
  onSelect,
  qualificationLoading,
  qualificationResults,
  selectOffer,
  updateFilter,
}) {
  const addressRequired =
    !addressSupplied && currentProduct && currentProduct.addressRequired
  const productsMatchingFilter = filterData.products.filter(p => p.id === filter.productId)
  const product = productsMatchingFilter.size === 1 ? productsMatchingFilter.first() : null

  let qualificationStatus = 0;
  let qualificationSpeedOK = true;
  let qualificationSpeedLimitReason = null;
  if (filter.productId && filter.speed) {
    const product = filterData.products.find(p => p.id === filter.productId)
    if (product) {
      qualificationStatus = qualificationResults.getIn([filter.productId, 'available'], product.addressRequired ? 'needs-addr' : false);
      qualificationSpeedLimitReason = qualificationResults.getIn([filter.productId, 'restrictions', 'speedLimitReason'], null);
      const maxDown = qualificationResults.getIn([filter.productId, 'restrictions', 'maxDown'], null);
      const maxUp   = qualificationResults.getIn([filter.productId, 'restrictions', 'maxUp'], null);
      const m = filter.get('speed').match(/Max|\d+/g);
      if (m && m.length === 2) {
        const down = m[0] === 'Max' ? 9999 : parseFloat(m[0]), up = parseFloat(m[1]);
        if (maxDown !== null && down > maxDown || maxUp !== null && up > maxUp) {
          qualificationSpeedOK = false;
        }
      }
    }
  }

  return (
    <section id="offer-selector">
      <Row>
        <Col sm={4} md={3}>
          <Panel>
            <Panel.Body>
              <Filter
                addressSupplied={addressSupplied}
                currentOffer={currentOffer}
                filterData={filterData}
                filter={filter}
                hideAddressButton={hideAddressButton}
                qualificationResults={qualificationResults}
                updateFilter={updateFilter}
              />
            </Panel.Body>
          </Panel>
        </Col>

        <Col sm={8} md={9}>
          {!addressSupplied && (
            <Panel bsStyle="warning" className="address-warning">
              <Panel.Body>
                {!hideAddressButton && (
                  <IndexLinkContainer to="/">
                    <Button
                      bsSize="small"
                      bsStyle="warning"
                      className="pull-right"
                    >
                      <Icon name="map-marker" /> Enter an address
                    </Button>
                  </IndexLinkContainer>
                )}
                With no address supplied we will only present you with virtual
                services.
              </Panel.Body>
            </Panel>
          )}

          {!currentOffer &&
            currentOfferId && (
              <Panel className="invalid-offer">
                <Panel.Body>
                  <Icon
                    size="5x"
                    name="exclamation-triangle"
                    className="pull-left"
                  />
                  <p>
                    You have selected an invalid or expired offer. Please choose
                    a new offer by picking a product to the left.
                  </p>
                </Panel.Body>
              </Panel>
            )}

          {!filter.productId && (
            <Panel className="select-product">
              <Panel.Body>
                <Icon size="5x" name="hand-o-left" className="pull-left" />
                <p>Select a product to the left to continue.</p>
              </Panel.Body>
            </Panel>
          )}

          {!!filter.productId && (
            <ProductDetails
              productId={filter.productId}
              qualificationLoading={qualificationLoading}
              qualificationResults={qualificationResults}
              qualificationSpeedLimitReason={qualificationSpeedLimitReason}
              qualificationSpeedOK={qualificationSpeedOK}
            />
          )}

          {!!filter.productId && (
            <OfferGrid
              currentOffer={currentOffer}
              filter={filter}
              selectOffer={selectOffer}
            />
          )}

          {addressRequired && (
            <Panel className="select-product" bsStyle="warning">
              <Panel.Body>
                <Icon size="5x" name="home" className="pull-left" />
                <p>
                  You have selected a service that requires an address to be
                  entered and did not enter an address in the previous step.
                  Please go back and enter an address to continue with the{' '}
                  <strong>{currentOffer.name}</strong> service.
                </p>
              </Panel.Body>
            </Panel>
          )}

          {onSelect &&
            currentOffer &&
            currentOfferVisible &&
            product &&
            (!product.addressRequired || (qualificationStatus >= 0 && qualificationSpeedOK)) && (
              <Panel bsStyle="info">
                <Panel.Body>
                  <Button
                    bsSize="large"
                    bsStyle="info"
                    className="pull-right continue-button"
                    disabled={addressRequired || qualificationLoading}
                    onClick={onSelect}
                  >
                    Continue with {currentOffer.name}{' '}
                    <Icon name="arrow-right" />
                  </Button>
                </Panel.Body>
              </Panel>
            )}
        </Col>
      </Row>
    </section>
  )
}

OfferSelector.propTypes = {
  addressSupplied: Pt.bool,
  currentOffer: Pt.instanceOf(OfferRecord),
  currentOfferId: Pt.number,
  currentOfferVisible: Pt.bool,
  currentProduct: Pt.instanceOf(ProductRecord),
  filterData: Pt.instanceOf(OfferFilterDataRecord).isRequired,
  filter: Pt.instanceOf(OfferFilterRecord).isRequired,
  hideAddressButton: Pt.bool,
  onSelect: Pt.func,
  qualificationLoading: Pt.bool.isRequired,
  qualificationResults: Ipt.map.isRequired,
  selectOffer: Pt.func.isRequired,
  updateFilter: Pt.func.isRequired,
}

export const makeMapStateToProps = () => {
  const selectOfferFilterData = makeSelectOfferFilterData()
  const selectProduct = makeSelectProduct()
  const selectQualificationResults = makeSelectQualificationResults()
  const selectLoading = makeSelectLoading()

  return (state, ownProps) => ({
    currentOfferVisible: ownProps.currentOffer
      ? filterOffer(ownProps.filter, ownProps.currentOffer)
      : false,
    currentProduct: ownProps.currentOffer
      ? selectProduct(state, {
        productId: ownProps.currentOffer.product,
      })
      : undefined,
    filterData: selectOfferFilterData(state, ownProps),
    qualificationLoading: selectLoading(state, { type: ['qualify'] }),
    qualificationResults: selectQualificationResults(state),
  })
}

export default connect(makeMapStateToProps)(OfferSelector)



// WEBPACK FOOTER //
// ./src/components/OfferSelector/index.jsx